import { Sprite, type Texture } from 'pixi.js';
import type { ProjectileBehavior } from '@/config/weapons';
import type { BulletMotion, MotionBody, SplitDef } from './bullet-motion';

export interface BulletSpawn {
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  speed: number;
  life: number;
  radius: number;
  damage: number;
  friendly: boolean;
  texKey: string;
  rotates: boolean;
  behavior?: ProjectileBehavior;
  drag?: number;
  turnRate?: number;
  aoeRadius?: number;
  aoeDamage?: number;
  /** Critical hit — pre-rolled at fire time, shown dark red (docs/12 F1) */
  crit?: boolean;
  /** Enemy motion recipe (docs/02 B3) — shared def data, never copied */
  motion?: BulletMotion;
  /** -1 mirrors the recipe's curl (fans curling both ways) */
  curveSign?: number;
}

/** Per-bullet impact event — feeds the floating damage numbers (docs/12 F1). */
export interface HitEvent {
  x: number;
  y: number;
  /** Hit circle (the target) — damage numbers merge per target (E1) */
  tx: number;
  ty: number;
  tr: number;
  damage: number;
  crit: boolean;
  friendly: boolean;
  /** The target reported what it really took (impact angle, soak, armour) */
  exact: boolean;
}

/** Area damage event produced when an AoE projectile dies (mortar/rocket). */
export interface AoeEvent {
  x: number;
  y: number;
  radius: number;
  damage: number;
  friendly: boolean;
}

/** One pooled bullet (state only; BulletSystem drives it). */
export class Bullet implements MotionBody {
  active = false;
  x = 0;
  y = 0;
  prevX = 0;
  prevY = 0;
  vx = 0;
  vy = 0;
  life = 0;
  radius = 0;
  damage = 0;
  friendly = false;
  rotates = false;
  texKey = '';
  behavior: ProjectileBehavior = 'straight';
  drag = 0;
  turnRate = 0;
  aoeRadius = 0;
  aoeDamage = 0;
  crit = false;
  // Enemy motion (docs/02 B3) — see bullet-motion.ts
  moving = false;
  hx = 1;
  hy = 0;
  age = 0;
  curve = 0;
  accel = 0;
  minSpeed = 0;
  maxSpeed = Infinity;
  rushAt = -1;
  rushAccel = 0;
  split: SplitDef | null = null;
  /** Time scale for ONE step (stasis fields set it, the step resets it) */
  slow = 1;
  // Enemy look (docs/04 danger language) — see bullet-look.ts
  frames: ReadonlyArray<Texture> | null = null;
  fused = false;
  /** Fuse length (s) of a fused shot — its split delay; 0 = no countdown */
  fuse = 0;
  /** Pulse frame offset so a volley does not throb in lockstep */
  phase = 0;
  readonly sprite = new Sprite();

  constructor() {
    this.sprite.visible = false;
  }
}
